<template>
  <a-modal
    v-model:open="visible"
    :title="title"
    :width="420"
    :footer="null"
    :maskClosable="false"
    :keyboard="false"
    :z-index="1100"
    centered
    class="otp-verify-modal"
    @cancel="close(null)"
  >
    <div class="otp-verify-content">
      <p v-if="description" class="otp-desc">{{ description }}</p>
      <!-- 로그인 2단계·등록 토큰이 만료(401)되면 고를 게 없음 → 안내와 주 버튼 「처음부터 다시 로그인」만(#24) -->
      <a-radio-group
        v-if="methods.length > 1 && !loginExpired"
        v-model:value="method"
        button-style="solid"
        class="otp-methods"
        @change="onMethodChange"
      >
        <a-radio-button v-for="m in methods" :key="m" :value="m">
          {{ methodLabel(m) }}
        </a-radio-button>
      </a-radio-group>
      <a-input
        v-if="isEnroll && method && !loginExpired"
        ref="targetInput"
        :value="isPhone ? formatPhone(target) : target"
        :type="isPhone ? 'tel' : 'email'"
        :autocomplete="isPhone ? 'tel' : 'email'"
        :placeholder="isPhone ? '휴대폰 번호 (010-0000-0000)' : '이메일 주소'"
        @input="onTargetInput"
        @pressEnter="requestCode"
      />
      <p v-if="guideText && !loginExpired" class="otp-guide">
        {{ guideText }}
      </p>
      <!-- 보낸 뒤: [인증번호 칸][다시 받기] 한 줄, 그 아래 주 버튼이 「확인」(D1) -->
      <div v-if="sent && !loginExpired" class="otp-row">
        <a-input
          ref="codeInput"
          :value="code"
          inputmode="numeric"
          autocomplete="one-time-code"
          placeholder="인증번호 6자리"
          @input="onCodeInput"
          @pressEnter="verify"
        />
        <a-button
          class="otp-resend"
          :disabled="loading || resendLeft > 0"
          @click="requestCode"
        >
          {{ resendLabel }}
        </a-button>
      </div>
      <p
        v-if="error"
        ref="errorText"
        class="otp-error"
        role="alert"
        tabindex="-1"
      >
        {{ error }}
      </p>
      <a-button
        ref="submitButton"
        type="primary"
        block
        class="otp-submit"
        :loading="loading"
        :disabled="submitDisabled"
        @click="submit"
      >
        {{ submitLabel }}
      </a-button>
      <p v-if="showHelp" class="otp-help">{{ notReceivedHelp }}</p>
    </div>
    <div v-if="!loginExpired" class="otp-footer">
      <a-button @click="close(null)">{{ cancelLabel }}</a-button>
    </div>
  </a-modal>
</template>

<script>
import axios from "axios";
import { message } from "ant-design-vue";
import {
  METHOD_LABELS,
  NOT_RECEIVED_HELP,
  RESET_HINT,
  authErrorMessage,
  formatPhone,
  profileMethods,
  setMfaToken,
  storeRenewedTokens,
} from "@/utils/mfa";

const ENROLL_METHODS = ["ALIMTALK_OTP", "FALLBACK_EMAIL_OTP"];
const ENROLL_REASONS = {
  OPERATOR: "서티라이프 운영팀 계정은 2차 인증이 필수입니다.",
  COMPANY_POLICY: "회사 설정으로 2차 인증이 필수입니다.",
};
// 문장 속 수단 이름. 둘 다 받침이 있어 '을'을 씀
const NOUNS = {
  ALIMTALK_OTP: "휴대폰",
  FALLBACK_EMAIL_OTP: "이메일",
  EMAIL_OTP: "이메일",
};
// 가린 값을 모를 때 받는 곳
const TO_MINE = {
  ALIMTALK_OTP: "내 휴대폰으로",
  FALLBACK_EMAIL_OTP: "내 이메일로",
  EMAIL_OTP: "내 이메일로",
};
// 본인 확인(step-up) 창 설명은 용도별(D5)
const STEP_UP_DESCRIPTIONS = {
  SEND: "메시지 발송·시나리오 저장 같은 작업 전에 본인 확인이 필요합니다. 인증하면 4시간 동안 다시 묻지 않습니다.",
  LOGIN:
    "보안이 중요한 작업이라 본인 확인이 한 번 더 필요합니다. 인증하면 하던 작업을 이어서 진행합니다.",
};

// 인증번호 창 하나로 세 가지를 처리(SPEC-SENDCERT-001 design.md §1·§6-1). start() 는 결과를 Promise 로 돌려주고, 닫으면 null.
// - login: 로그인 2단계(twoFactorToken, A3·A4). 결과 = verify 응답(로그인 응답 모양).
// - stepUp: 세션 중 본인 확인(A5·A6). 결과 = true(증표 저장 뒤). challenge 가 TWO_FACTOR_NOT_ASSIGNED 면 enroll 로 바뀜.
// - enroll: 수단 등록. enrollToken 이 있으면 로그인 중(A12·A13, 결과 = verify 응답),
//   없으면 세션 중(A14·A15, 결과 = true, 새 증표·토큰 저장). currentMasked 가 있으면 「변경」.
// 짜임새는 세 앱 공통 결정(D1~D6): 받는 곳 → [인증번호 칸][다시 받기] → 폭 가득 주 버튼 → 「취소」, 보내기 전엔 「인증번호 받기」가 주 버튼.
// 세션 중 호출은 전역 this.$apiInstance 를 씀: "@/axios" 를 직접 import 하면 App → 이 창 → axios → store → router →
// Signin(이 창을 components 로 씀) 순환이 생겨 앱 시작 때 초기화 순서 오류가 남.
// admin-hospital·admin-manufacturer 가 같은 파일을 씀.
export default {
  name: "OtpVerifyModal",
  data() {
    return {
      visible: false,
      ctx: {},
      method: "",
      extraMethods: [],
      target: "",
      sent: false,
      code: "",
      targetMasked: "",
      expiresMin: 5,
      error: "",
      loading: false,
      profileLoading: false,
      resendLeft: 0,
      timer: null,
      resolver: null,
      loginExpired: false,
      notReceivedHelp: NOT_RECEIVED_HELP,
    };
  },
  computed: {
    isLogin() {
      return this.ctx.mode === "login";
    },
    isEnroll() {
      return this.ctx.mode === "enroll";
    },
    isStepUp() {
      return this.ctx.mode === "stepUp";
    },
    duringLogin() {
      return this.isLogin || !!this.ctx.enrollToken;
    },
    isPhone() {
      return this.method === "ALIMTALK_OTP";
    },
    title() {
      if (this.isEnroll) {
        if (this.ctx.enrollToken || this.ctx.purpose) return "2차 인증 등록";
        return `${NOUNS[this.method] || "2차 인증"} ${
          this.ctx.currentMasked ? "변경" : "등록"
        }`;
      }
      return this.isStepUp ? "본인 확인" : "2차 인증";
    },
    description() {
      if (this.isEnroll) {
        if (this.ctx.enrollToken) {
          return `${
            ENROLL_REASONS[this.ctx.reason] || "2차 인증이 필요한 계정입니다."
          } 휴대폰이나 이메일을 등록하면 로그인이 끝납니다.`;
        }
        if (this.ctx.purpose) {
          return "이 작업을 하려면 2차 인증 수단이 필요합니다. 휴대폰이나 이메일을 등록하면 하던 작업을 이어서 진행합니다.";
        }
        if (this.ctx.currentMasked) {
          return `지금 등록된 ${this.ctx.currentMasked} 대신 새 ${
            this.isPhone ? "번호를" : "이메일을"
          } 등록합니다.`;
        }
        return `등록한 ${
          this.isPhone ? "휴대폰으로" : "이메일로"
        } 로그인할 때 인증번호를 받습니다.`;
      }
      if (this.isLogin) return "로그인하려면 2차 인증이 필요합니다.";
      return (
        STEP_UP_DESCRIPTIONS[this.ctx.purpose] || STEP_UP_DESCRIPTIONS.LOGIN
      );
    },
    methods() {
      const base = this.isEnroll
        ? (this.ctx.availableMethods || ENROLL_METHODS).filter((m) =>
            ENROLL_METHODS.includes(m)
          )
        : (this.ctx.availableMethods || []).filter((m) => METHOD_LABELS[m]);
      // step-up 은 프로필에 수단이 없거나 못 읽으면 휴대폰으로 시도 → 수단이 없으면 서버가 TWO_FACTOR_NOT_ASSIGNED
      const list = this.isStepUp && !base.length ? ["ALIMTALK_OTP"] : base;
      return [...new Set([...list, ...this.extraMethods])];
    },
    // 받는 곳 줄 → 보낸 뒤엔 "…(으)로 보냈습니다. N분 안에"(D1, D5 '내 휴대폰')
    guideText() {
      if (this.profileLoading) return "인증 수단을 확인하는 중입니다.";
      if (!this.method) return "";
      if (this.sent) {
        return `${
          this.targetMasked ||
          this.targetOf(this.method) ||
          METHOD_LABELS[this.method]
        }(으)로 인증번호를 보냈습니다. ${this.expiresMin}분 안에 입력하세요.`;
      }
      if (this.isEnroll) {
        return this.isPhone
          ? "등록할 휴대폰 번호를 넣고 카카오 알림톡으로 인증번호를 받으세요."
          : "등록할 이메일 주소를 넣고 인증번호를 받으세요.";
      }
      const target = this.targetOf(this.method);
      return target
        ? `내 ${NOUNS[this.method]} ${target}(으)로 인증번호를 보냅니다.`
        : `${TO_MINE[this.method]} 인증번호를 보냅니다.`;
    },
    // 대기 중이면 남은 초를 버튼에(D3)
    sendLabel() {
      return this.resendLeft > 0
        ? `인증번호 받기 (${this.resendLeft}초)`
        : "인증번호 받기";
    },
    resendLabel() {
      return this.resendLeft > 0
        ? `다시 받기 (${this.resendLeft}초)`
        : "다시 받기";
    },
    submitLabel() {
      if (this.loginExpired) return "처음부터 다시 로그인";
      if (!this.sent) return this.sendLabel;
      if (!this.isEnroll) return "확인";
      if (this.ctx.enrollToken) return "등록하고 로그인";
      if (this.ctx.purpose) return "등록하고 계속";
      return this.ctx.currentMasked ? "변경" : "등록";
    },
    submitDisabled() {
      if (this.loginExpired) return false;
      if (this.loading || this.profileLoading || !this.method) return true;
      return this.sent ? this.code.length !== 6 : this.resendLeft > 0;
    },
    cancelLabel() {
      return this.duringLogin ? "처음부터 다시 로그인" : "취소";
    },
    // 발송 실패 문구 끝에 같은 안내가 붙었으면 아래 회색 줄은 숨김(같은 문장 두 번 방지)
    showHelp() {
      return (
        !this.isEnroll && !this.loginExpired && !this.error.includes(RESET_HINT)
      );
    },
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
  methods: {
    formatPhone,
    start(ctx) {
      this.resolver?.(null);
      this.ctx = ctx;
      this.reset();
      this.visible = true;
      if (this.isStepUp) this.loadProfileMethods();
      else this.focus(this.isEnroll ? "targetInput" : "submitButton");
      return new Promise((resolve) => {
        this.resolver = resolve;
      });
    },
    reset() {
      this.extraMethods = [];
      this.target = "";
      this.profileLoading = false;
      this.loginExpired = false;
      this.method = this.methods[0] || "";
      this.resetSent();
      if (!this.method)
        this.error =
          "이 화면에서 쓸 수 있는 인증 수단이 없습니다. 고객센터에 문의해 주세요.";
    },
    close(result) {
      clearInterval(this.timer);
      this.visible = false;
      const resolve = this.resolver;
      this.resolver = null;
      resolve?.(result);
    },
    // keepTimer: 만료 뒤에도 재요청 대기 초는 그대로 셈(D3)
    resetSent(keepTimer = false) {
      if (!keepTimer) {
        clearInterval(this.timer);
        this.resendLeft = 0;
      }
      this.sent = false;
      this.code = "";
      this.targetMasked = "";
      this.error = "";
    },
    // 칸이 보이면 커서를 둠(D2). 창이 그려지고(다시 열 때는 숨김이 풀리고), 앞 확인창(sweetalert)이 닫히며 커서를 표로 돌려놓고,
    // ant 가 창이 다 열린 뒤 창 테두리에 커서를 두기까지 0.5초쯤 걸림 → 보이면 한 번 옮기고, 그 뒤 0.6초 동안은
    // 커서가 창 내용 밖으로 밀려났을 때만 되돌림(사용자가 창 안 다른 칸을 누른 건 그대로).
    focus(ref, tries = 12, placed = false) {
      this.$nextTick?.(() => {
        const target = this.$refs[ref];
        const el = target?.$el || target;
        const content = el?.closest?.(".ant-modal-content");
        if (el?.offsetParent && (!placed || !content?.contains(document.activeElement))) {
          (target.focus ? target : el).focus();
          placed = true;
        }
        if (tries > 1) setTimeout(() => this.focus(ref, tries - 1, placed), 50);
      });
    },
    // 오류 뒤 커서 자리. 주 버튼이 대기(NN초)로 꺼져 있으면 포커스를 못 받아 창 밖(BODY)으로 빠짐 → 오류 문장에 둠
    focusAfterError() {
      if (this.loginExpired) return this.focus("submitButton");
      if (this.sent) return this.focus("codeInput");
      this.focus(this.resendLeft > 0 ? "errorText" : "submitButton");
    },
    // step-up 창의 수단 = 프로필 mfa 의 휴대폰·이메일(design.md §2-3). 못 읽으면 기본(휴대폰)으로 둠.
    // 수단이 하나도 없으면 바로 한 번 요청: 2차 인증 예외면 그대로 통과, 아니면 TWO_FACTOR_NOT_ASSIGNED → 등록 화면(#6)
    async loadProfileMethods() {
      const ctx = this.ctx;
      let factors = null;
      this.profileLoading = true;
      try {
        const { data } = await this.$apiInstance.get("/auth/profile");
        if (this.ctx !== ctx) return;
        if (data?.mfa) {
          factors = profileMethods(data.mfa);
          this.ctx = {
            ...ctx,
            availableMethods: factors,
            phoneMasked: data.mfa.phoneMasked,
            emailMasked: data.mfa.fallbackEmailMasked,
          };
          if (!this.sent && !this.methods.includes(this.method))
            this.method = this.methods[0];
        }
      } catch (error) {
        console.log(error);
        if (this.ctx !== ctx) return;
      }
      this.profileLoading = false;
      if (factors && !factors.length) return this.requestCode();
      this.focus("submitButton");
    },
    // 세션은 2FA 필요인데 계정에 수단이 없음 → 같은 창을 등록으로(design.md §1-3, D-104)
    toEnroll() {
      this.ctx = { mode: "enroll", purpose: this.ctx.purpose };
      this.reset();
      this.focus("targetInput");
    },
    methodLabel(method) {
      return METHOD_LABELS[method];
    },
    targetOf(method) {
      if (this.isEnroll) return "";
      return {
        ALIMTALK_OTP: this.ctx.phoneMasked,
        FALLBACK_EMAIL_OTP: this.ctx.emailMasked,
        EMAIL_OTP: this.ctx.emails?.[0]?.email,
      }[method];
    },
    onMethodChange() {
      this.target = "";
      this.resetSent();
      this.focus(this.isEnroll ? "targetInput" : "submitButton");
    },
    // 휴대폰은 숫자만 11자리까지 담고 칸에는 010-0000-0000 으로 보임. 붙여넣은 "010 5452-7892" 도 그대로 받음
    onTargetInput(event) {
      const value = event.target.value.trim();
      this.target = this.isPhone
        ? value.replace(/[^0-9]/g, "").slice(0, 11)
        : value;
      // 걸러낸 값이 전과 같으면(12번째 숫자) ant-design-vue 4.2 는 칸을 다시 그리지 않음 → 칸 글자를 직접 맞춤
      if (this.isPhone) event.target.value = formatPhone(this.target);
      // 대상이 바뀌면 보낸 인증번호는 새 대상과 맞지 않음
      if (this.sent) this.resetSent();
    },
    // maxlength 를 두지 않음: "123-456"·" 123456" 을 붙여넣어도 숫자만 6자리로(D2)
    onCodeInput(event) {
      this.code = event.target.value.replace(/[^0-9]/g, "").slice(0, 6);
      // 7번째 숫자: 값이 그대로라 ant-design-vue 4.2 가 칸을 다시 그리지 않음 → 칸 글자를 직접 맞춤
      event.target.value = this.code;
    },
    targetError() {
      if (this.isPhone) {
        return /^010\d{8}$/.test(this.target)
          ? ""
          : "휴대폰 번호는 010으로 시작하는 숫자 11자리로 입력해 주세요.";
      }
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.target)
        ? ""
        : "이메일 주소를 확인해 주세요.";
    },
    startTimer(seconds) {
      clearInterval(this.timer);
      this.resendLeft = seconds;
      this.timer = setInterval(() => {
        this.resendLeft -= 1;
        if (this.resendLeft <= 0) clearInterval(this.timer);
      }, 1000);
    },
    post(step, extra = {}) {
      const body = { method: this.method, ...extra };
      // 로그인 중(2단계·등록)은 access 토큰 갱신 인터셉터를 타지 않는 axios 를 씀
      const server = process.env.VUE_APP_SERVERURL;
      if (this.isEnroll) {
        if (step === "challenge") body.target = this.target;
        if (this.ctx.enrollToken) {
          return axios.post(`${server}/auth/2fa/enroll/${step}`, {
            enrollToken: this.ctx.enrollToken,
            ...body,
          });
        }
        // SEND 창에서 등록하면 확인(A15)에 purpose:'SEND' → 증표에 sendAt 이 들어가 재전송이 바로 통과(D-124)
        if (step === "verify" && this.ctx.purpose === "SEND")
          body.purpose = "SEND";
        // step-up 창 안의 등록이 다시 step-up 창을 열면 서로 기다리므로 막음(axios.js 의 _stepUp)
        return this.$apiInstance.post(
          `/auth/2fa/factors/${step}`,
          body,
          this.ctx.purpose ? { _stepUp: ["LOGIN", "SEND"] } : undefined
        );
      }
      if (this.isLogin) {
        body.twoFactorToken = this.ctx.twoFactorToken;
        // emailId 는 challenge 에만: verify DTO 엔 없어 inapi 가 400(forbidNonWhitelisted)
        if (step === "challenge" && this.method === "EMAIL_OTP")
          body.emailId = this.ctx.emails?.[0]?.emailId;
        return axios.post(`${server}/auth/2fa/${step}`, body);
      }
      return this.$apiInstance.post(`/auth/2fa/step-up/${step}`, {
        purpose: this.ctx.purpose,
        ...body,
      });
    },
    failMessage(error, fallback) {
      const data = error.response?.data || {};
      if (this.duringLogin && error.response?.status === 401) {
        // 다시 눌러도 같은 401 → 주 버튼을 「처음부터 다시 로그인」으로(#24)
        this.loginExpired = true;
        return "인증 시간이 지났습니다. 처음부터 다시 로그인해 주세요.";
      }
      if (this.isEnroll && data.code === "GEO_BLOCKED") {
        return "해외에서는 2차 인증을 등록할 수 없습니다. 국내에서 등록해 주세요.";
      }
      const text = authErrorMessage(error) || data.message || fallback;
      // 받을 수 있는 다른 수단이 없는 발송 실패 → 회사 관리자 초기화 안내를 붙임(D4)
      const noOther = !(data.fallbackMethods || []).some(
        (m) => METHOD_LABELS[m] && m !== this.method
      );
      return !this.isEnroll && data.code === "OTP_SEND_FAILED" && noOther
        ? `${text} ${RESET_HINT}`
        : text;
    },
    submit() {
      if (this.loginExpired) return this.close(null);
      return this.sent ? this.verify() : this.requestCode();
    },
    async requestCode() {
      if (this.loading || this.resendLeft > 0 || !this.method) return;
      if (this.isEnroll && this.targetError()) {
        this.error = this.targetError();
        this.focus("targetInput");
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        const { data } = await this.post("challenge");
        // 2FA 예외가 유효하면 step-up 이 인증번호 없이 증표를 줌
        if (this.isStepUp && data?.mfaToken) {
          setMfaToken(data.mfaToken);
          this.close(true);
          return;
        }
        this.sent = true;
        this.code = "";
        this.targetMasked = data?.targetMasked || "";
        this.expiresMin = Math.max(
          1,
          Math.round((data?.expiresInSec || 300) / 60)
        );
        this.startTimer(data?.resendAfterSec || 60);
        this.focus("codeInput");
      } catch (error) {
        const data = error.response?.data || {};
        if (this.isStepUp && data.code === "TWO_FACTOR_NOT_ASSIGNED") {
          this.toEnroll();
          return;
        }
        if (data.code === "OTP_RATE_LIMITED")
          this.startTimer(data.retryAfterSec || 60);
        this.error = this.failMessage(error, "인증번호를 보내지 못했습니다.");
        // 발송 실패면 서버가 알려 준 계정의 다른 수단으로 바꿔 줌(등록 중이면 빈 목록)
        const others = (data.fallbackMethods || []).filter(
          (m) => METHOD_LABELS[m] && m !== this.method
        );
        if (data.code === "OTP_SEND_FAILED" && others.length) {
          this.extraMethods = [...new Set([...this.extraMethods, ...others])];
          this.method = others[0];
        }
        this.focusAfterError();
      } finally {
        this.loading = false;
      }
    },
    async verify() {
      if (this.loading || this.code.length !== 6) return;
      this.loading = true;
      this.error = "";
      try {
        const { data } = await this.post("verify", { code: this.code });
        if (this.isEnroll) {
          message.success(
            this.ctx.currentMasked
              ? `${NOUNS[this.method]}을 바꿨습니다.`
              : `${NOUNS[this.method]}을 2차 인증 수단으로 등록했습니다.`
          );
        }
        if (this.duringLogin) {
          this.close(data);
          return;
        }
        if (this.isEnroll) {
          storeRenewedTokens(data);
          this.close(true);
          return;
        }
        setMfaToken(data?.mfaToken);
        this.close(!!data?.mfaToken);
      } catch (error) {
        const expired = error.response?.data?.code === "OTP_EXPIRED";
        if (expired) this.resetSent(true);
        this.error = this.failMessage(error, "인증에 실패했습니다.");
        this.focusAfterError();
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
// 앱 공통 .ant-modal-content { padding: 0 } 때문에 본문이 창 가장자리에 붙음 → 제목 줄(왼쪽 20px)과 같은 여백(#3).
// 하단 「취소」·「닫기」 줄(.otp-footer)은 「내 계정 보안」 창(Sidenav)도 같이 씀.
.otp-verify-modal {
  .ant-modal-body {
    padding: 4px 20px 20px;
  }
  .otp-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 20px;
  }
}
.otp-verify-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  p {
    margin: 0;
    word-break: keep-all;
  }
  .otp-desc {
    color: #2f3337;
    font-size: 14px;
    line-height: 1.6;
  }
  // 고른 수단은 앱 주 색(노랑) 바탕에 진한 글자(D8)
  .otp-methods {
    display: flex;
    width: 100%;
    .ant-radio-button-wrapper {
      flex: 1;
      height: 40px;
      line-height: 38px;
      text-align: center;
      color: #4b5158;
      &:hover {
        color: #17191c;
      }
    }
    .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
      background: #ffe414;
      border-color: #ffe414;
      color: #17191c;
      font-weight: 700;
      &::before {
        background-color: #ffe414;
      }
    }
  }
  .ant-input {
    height: 40px;
    font-size: 15px;
  }
  .otp-guide {
    color: #4b5158;
    font-size: 13px;
    line-height: 1.5;
  }
  .otp-row {
    display: flex;
    gap: 8px;
    .otp-resend {
      flex: none;
      height: 40px;
      padding: 0 14px;
      font-size: 13px;
    }
  }
  .otp-error {
    color: #d92d20;
    font-size: 13px;
    line-height: 1.5;
    outline: none;
  }
  .otp-submit {
    height: 44px;
    font-size: 15px;
  }
  // 앱 공통 hover(!important)가 비활성 주 버튼도 진한 노랑으로 칠함(#18)
  .otp-submit:disabled,
  .otp-submit:disabled:hover {
    background-color: #e8ebed !important;
    border-color: #e8ebed !important;
    span {
      color: #8d939a;
    }
  }
  .otp-help {
    color: #6e747c;
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
